/**
 * Right-side "Детали" panel for the editor V2 rewrite (task 6).
 *
 * Ported from the `VideoEditor.tsx` monolith's right `<aside>` (~line 2083+):
 * an Информация block (duration/format/aspect-ratio picker/clip & layer
 * counts), a Слои block (layer list with select/delete), and — new for V2 —
 * a consolidated inspector that replaces the monolith's
 * per-tool sidebars (`TextLayerControls` / `StickerTextControls` /
 * `AssetLayerControls` / `EffectLayerControls` / `AudioPanel`) with a single
 * consolidated property editor, re-skinned dark and wired to the store.
 *
 * Layer timing is edited directly on the timeline with move/trim gestures.
 */
'use client';

import {
  Bold,
  ChevronLeft,
  ChevronRight,
  Diamond,
  Film,
  Italic,
  Move,
  Music,
  Sparkles,
  Sticker,
  Trash2,
  Type,
  Underline,
  Volume2,
} from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import {
  selectSelectedClip,
  selectSelectedLayer,
  selectDuration,
  useEditorStore,
} from '@/lib/editor/editorStore';
import type { EditorOverlayLayer, EditorOverlayLayerType } from '@/types/editor';
import {
  clipFit,
  clipOpacity,
  clipPlaybackRate,
  clipPositionX,
  clipPositionY,
  clipRotation,
  clipScale,
} from '@/lib/editor/clipProperties';
import { FontPicker } from './FontPicker';
import { EditorColorPicker } from './EditorColorPicker';
import { EditorRangeControl } from './EditorRangeControl';
import { cn } from '@/lib/utils/cn';
import { frameDuration } from '@/lib/editor/frameGrid';
import { cleanLayerKeyframes, layerVisualAt } from '@/lib/editor/layerKeyframes';

const LAYER_ICON: Record<EditorOverlayLayerType, typeof Type> = {
  text: Type,
  sticker: Sticker,
  effect: Sparkles,
  asset: Sticker,
};

const LAYER_DOT: Record<EditorOverlayLayerType, string> = {
  text: 'bg-slate-400',
  effect: 'bg-blue-400',
  asset: 'bg-emerald-400',
  sticker: 'bg-brand-400',
};
const AUDIO_METER_SEGMENTS = Array.from({ length: 12 }, (_, index) => index + 1);

function formatTimecode(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function EditorDetailsPanel() {
  const { t } = useI18n();

  // ---- store reads
  const layers = useEditorStore((s) => s.layers);
  const audioTrack = useEditorStore((s) => s.audioTrack);
  const crossfadeDuration = useEditorStore((s) => s.crossfadeDuration);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const selectedLayer = useEditorStore((s) => selectSelectedLayer(s));
  const selectedClip = useEditorStore((s) => selectSelectedClip(s));
  const activeTool = useEditorStore((s) => s.activeTool);
  const currentTime = useEditorStore((s) => s.currentTime);
  const projectDuration = useEditorStore((s) => selectDuration(s));
  const isPlaying = useEditorStore((s) => s.isPlaying);

  // ---- store actions
  const setCrossfade = useEditorStore((s) => s.setCrossfade);
  const updateLayer = useEditorStore((s) => s.updateLayer);
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const removeLayer = useEditorStore((s) => s.removeLayer);
  const addLayerKeyframe = useEditorStore((s) => s.addLayerKeyframe);
  const removeLayerKeyframe = useEditorStore((s) => s.removeLayerKeyframe);
  const setCurrentTime = useEditorStore((s) => s.setCurrentTime);
  const setAudioTrack = useEditorStore((s) => s.setAudioTrack);
  const removeAudioTrack = useEditorStore((s) => s.removeAudioTrack);
  const updateClip = useEditorStore((s) => s.updateClip);

  const patchLayer = (patch: Partial<EditorOverlayLayer>) => {
    if (!selectedLayer) return;
    updateLayer(selectedLayer.id, patch);
  };

  const patchAudio = (patch: Partial<NonNullable<typeof audioTrack>>) => {
    if (!audioTrack) return;
    setAudioTrack({ ...audioTrack, ...patch });
  };

  const selectedLayerIsImage = Boolean(
    selectedLayer?.src && (selectedLayer.type === 'asset' || selectedLayer.type === 'sticker')
  );
  const selectedLayerVisual = selectedLayer ? layerVisualAt(selectedLayer, currentTime) : null;
  const selectedLayerKeyframes = selectedLayer ? cleanLayerKeyframes(selectedLayer.keyframes, selectedLayer) : [];
  const currentLayerKeyframe = selectedLayerKeyframes.find(
    (keyframe) => Math.abs(keyframe.time - currentTime) <= frameDuration() / 2
  );
  const previousLayerKeyframe = [...selectedLayerKeyframes]
    .reverse()
    .find((keyframe) => keyframe.time < currentTime - frameDuration() / 2);
  const nextLayerKeyframe = selectedLayerKeyframes.find(
    (keyframe) => keyframe.time > currentTime + frameDuration() / 2
  );
  const showAudioInspector = activeTool === 'audio';
  const audioLocalTime = audioTrack ? currentTime - (audioTrack.start ?? 0) : -1;
  const audioWindow = audioTrack?.timelineDuration ?? audioTrack?.duration ?? projectDuration;
  const audioMeterLevel = audioTrack && isPlaying && audioLocalTime >= 0 && audioLocalTime <= audioWindow && !audioTrack.muted
    ? Math.min(1, audioTrack.volume * (0.42 + 0.58 * Math.abs(Math.sin(currentTime * 8.7))))
    : 0;

  return (
    <aside className="flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-white/10 bg-[#121214] text-white">
      <div className="flex h-10 shrink-0 items-center border-b border-white/10 px-3.5">
        <p className="text-sm font-semibold">{t('studio.editor.details')}</p>
      </div>

      <div data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto p-3">
        {/* ---- Слои ---------------------------------------------------------- */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <p className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
            {t('studio.editor.layersSection')}
          </p>
          {layers.length ? (
            <div className="space-y-1.5">
              {layers.map((layer) => {
                const Icon = LAYER_ICON[layer.type];
                const selected = selectedLayerId === layer.id;
                return (
                  <div
                    key={layer.id}
                    className={cn(
                      'flex items-start gap-2 rounded-lg border px-2.5 py-2 transition',
                      selected ? 'border-brand-500 bg-brand-500/15' : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => selectLayer(layer.id)}
                      className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    >
                      <span className={cn('mt-1 h-1.5 w-1.5 shrink-0 rounded-full', LAYER_DOT[layer.type])} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-1.5 truncate text-xs font-semibold">
                            <Icon className="h-3 w-3 shrink-0 text-white/50" strokeWidth={1.8} />
                            {layer.label}
                          </span>
                          <span className="shrink-0 text-[10px] font-medium tabular-nums text-white/40">
                            {formatTimecode(layer.start)}–{formatTimecode(layer.end)}
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-white/40">{layer.text}</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLayer(layer.id)}
                      aria-label="Удалить слой"
                      className="shrink-0 rounded-full p-1.5 text-white/40 transition hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="rounded-lg bg-white/[0.02] px-3 py-3 text-center text-[11px] text-white/40">
              {t('studio.editor.noLayers')}
            </p>
          )}
        </section>

        {/* ---- Единственный инспектор выбранного объекта --------------------- */}
        <section className="mt-2.5 rounded-xl border border-white/10 bg-white/[0.03]">
          <div className="border-b border-white/10 px-3 py-2.5">
            <p className="text-xs font-semibold text-white/80">Настройки</p>
            <p className="mt-0.5 text-[10px] text-white/35">Длительность меняется ручками на таймлайне</p>
          </div>

          <div className="space-y-3 p-3">
            {selectedClip && !showAudioInspector && (
                  <div className="rounded-lg border border-white/10 bg-black/15 p-2.5">
                    <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
                      <Film className="h-3 w-3" /> Выбранный клип
                    </p>
                    <p className="mb-2 truncate text-xs font-semibold text-white/80">{selectedClip.title}</p>
                    <Field label="Громкость видео">
                      <EditorRangeControl
                        min={0}
                        max={1}
                        step={0.05}
                        value={selectedClip.volume ?? 1}
                        ariaLabel="Громкость выбранного видео"
                        format={(v) => `${Math.round(v * 100)}%`}
                        onChange={(volume) => updateClip(selectedClip.id, { volume })}
                      />
                    </Field>
                    <div className="mt-3 space-y-3 border-t border-white/10 pt-3">
                      <Field label="Скорость">
                        <EditorRangeControl
                          min={0.25}
                          max={4}
                          step={0.05}
                          value={clipPlaybackRate(selectedClip)}
                          ariaLabel="Скорость выбранного видео"
                          format={(v) => `${Number(v.toFixed(2))}×`}
                          onChange={(playbackRate) => updateClip(selectedClip.id, { playbackRate })}
                        />
                      </Field>
                      <Field label="Заполнение кадра">
                        <div className="grid grid-cols-2 gap-1.5">
                          {([
                            ['contain', 'Вписать'],
                            ['cover', 'Заполнить'],
                          ] as const).map(([fit, label]) => (
                            <button
                              key={fit}
                              type="button"
                              onClick={() => updateClip(selectedClip.id, { fit })}
                              className={cn(
                                'h-8 rounded-lg border text-[11px] font-semibold transition',
                                clipFit(selectedClip) === fit
                                  ? 'border-brand-500 bg-brand-500/15 text-white'
                                  : 'border-white/10 bg-white/5 text-white/50 hover:text-white'
                              )}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </Field>
                      <Field label="Масштаб">
                        <EditorRangeControl
                          min={0.25}
                          max={4}
                          step={0.05}
                          value={clipScale(selectedClip)}
                          ariaLabel="Масштаб выбранного видео"
                          format={(v) => `${Math.round(v * 100)}%`}
                          onChange={(scale) => updateClip(selectedClip.id, { scale })}
                        />
                      </Field>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Позиция X">
                          <EditorRangeControl
                            min={0}
                            max={100}
                            step={1}
                            value={clipPositionX(selectedClip)}
                            ariaLabel="Позиция видео по горизонтали"
                            unit="%"
                            onChange={(positionX) => updateClip(selectedClip.id, { positionX })}
                          />
                        </Field>
                        <Field label="Позиция Y">
                          <EditorRangeControl
                            min={0}
                            max={100}
                            step={1}
                            value={clipPositionY(selectedClip)}
                            ariaLabel="Позиция видео по вертикали"
                            unit="%"
                            onChange={(positionY) => updateClip(selectedClip.id, { positionY })}
                          />
                        </Field>
                      </div>
                      <Field label="Поворот">
                        <EditorRangeControl
                          min={-180}
                          max={180}
                          step={1}
                          value={clipRotation(selectedClip)}
                          ariaLabel="Поворот выбранного видео"
                          unit="°"
                          onChange={(rotation) => updateClip(selectedClip.id, { rotation })}
                        />
                      </Field>
                      <Field label="Прозрачность">
                        <EditorRangeControl
                          min={0}
                          max={1}
                          step={0.01}
                          value={clipOpacity(selectedClip)}
                          ariaLabel="Прозрачность выбранного видео"
                          format={(v) => `${Math.round(v * 100)}%`}
                          onChange={(opacity) => updateClip(selectedClip.id, { opacity })}
                        />
                      </Field>
                      <button
                        type="button"
                        onClick={() => updateClip(selectedClip.id, {
                          fit: 'contain',
                          positionX: 50,
                          positionY: 50,
                          scale: 1,
                          rotation: 0,
                          opacity: 1,
                        })}
                        className="h-8 w-full rounded-lg border border-white/10 bg-white/5 text-[11px] font-semibold text-white/55 transition hover:border-white/20 hover:text-white"
                      >
                        Сбросить кадрирование
                      </button>
                    </div>
                  </div>
            )}

            {selectedLayer && !showAudioInspector ? (
                  <>
                    <div className="rounded-lg border border-white/10 bg-black/15 p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/45">
                            <Diamond className="h-3 w-3 fill-brand-400 text-brand-400" /> Анимация
                          </p>
                          <p className="mt-1 text-[10px] text-white/35">
                            {selectedLayerKeyframes.length
                              ? `${selectedLayerKeyframes.length} ключ. кадр.`
                              : 'Добавьте кадры для движения и масштаба'}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => previousLayerKeyframe && setCurrentTime(previousLayerKeyframe.time)}
                            disabled={!previousLayerKeyframe}
                            aria-label="Предыдущий ключевой кадр"
                            className="grid h-7 w-7 place-items-center rounded-md border border-white/10 bg-white/5 text-white/60 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-25"
                          >
                            <ChevronLeft className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => addLayerKeyframe(selectedLayer.id)}
                            aria-label={currentLayerKeyframe ? 'Обновить ключевой кадр' : 'Добавить ключевой кадр'}
                            title={currentLayerKeyframe ? 'Обновить кадр' : 'Добавить кадр'}
                            className={cn(
                              'grid h-7 w-7 place-items-center rounded-md border transition',
                              currentLayerKeyframe
                                ? 'border-brand-400 bg-brand-500/25 text-brand-200'
                                : 'border-white/10 bg-white/5 text-white/60 hover:border-brand-400/60 hover:text-white'
                            )}
                          >
                            <Diamond className={cn('h-3.5 w-3.5', currentLayerKeyframe && 'fill-current')} />
                          </button>
                          <button
                            type="button"
                            onClick={() => nextLayerKeyframe && setCurrentTime(nextLayerKeyframe.time)}
                            disabled={!nextLayerKeyframe}
                            aria-label="Следующий ключевой кадр"
                            className="grid h-7 w-7 place-items-center rounded-md border border-white/10 bg-white/5 text-white/60 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-25"
                          >
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                          {currentLayerKeyframe && (
                            <button
                              type="button"
                              onClick={() => removeLayerKeyframe(selectedLayer.id, currentLayerKeyframe.id)}
                              aria-label="Удалить ключевой кадр"
                              className="grid h-7 w-7 place-items-center rounded-md border border-red-500/20 bg-red-500/10 text-red-400 transition hover:bg-red-500/20"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {(selectedLayer.type === 'text' || (selectedLayer.type === 'sticker' && !selectedLayer.src)) && (
                      <Field label={t('studio.editor.textContent') || 'Текст'}>
                        <textarea
                          value={selectedLayer.text}
                          onChange={(e) => patchLayer({ text: e.target.value })}
                          rows={2}
                          className="w-full resize-none rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-white outline-none focus:border-brand-500"
                        />
                      </Field>
                    )}

                    {(selectedLayer.type === 'text' || (selectedLayer.type === 'sticker' && !selectedLayer.src)) && (
                      <Field label="Шрифт">
                        <FontPicker
                          value={selectedLayer.fontFamily}
                          onChange={(fontFamily) => patchLayer({ fontFamily })}
                        />
                      </Field>
                    )}

                    <Field label={selectedLayerIsImage ? 'Ширина' : 'Размер'}>
                      <EditorRangeControl
                        min={selectedLayerIsImage ? 8 : 12}
                        max={selectedLayerIsImage ? 90 : 600}
                        step={1}
                        value={selectedLayerIsImage ? (selectedLayerVisual?.widthPercent ?? 26) : (selectedLayerVisual?.size ?? selectedLayer.size)}
                        ariaLabel={selectedLayerIsImage ? 'Ширина слоя' : 'Размер слоя'}
                        unit={selectedLayerIsImage ? '%' : 'px'}
                        onChange={(v) =>
                          patchLayer(selectedLayerIsImage ? { widthPercent: v } : { size: v })
                        }
                      />
                    </Field>

                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Позиция X">
                        <EditorRangeControl
                          min={0}
                          max={100}
                          step={1}
                          value={selectedLayerVisual?.x ?? selectedLayer.x}
                          ariaLabel="Позиция слоя по горизонтали"
                          unit="%"
                          onChange={(x) => patchLayer({ x })}
                        />
                      </Field>
                      <Field label="Позиция Y">
                        <EditorRangeControl
                          min={0}
                          max={100}
                          step={1}
                          value={selectedLayerVisual?.y ?? selectedLayer.y}
                          ariaLabel="Позиция слоя по вертикали"
                          unit="%"
                          onChange={(y) => patchLayer({ y })}
                        />
                      </Field>
                    </div>

                    <Field label="Поворот">
                      <EditorRangeControl
                        min={-180}
                        max={180}
                        step={1}
                        value={selectedLayerVisual?.rotation ?? selectedLayer.rotation ?? 0}
                        ariaLabel="Поворот слоя"
                        unit="°"
                        onChange={(rotation) => patchLayer({ rotation })}
                      />
                    </Field>

                    <p className="flex items-center gap-1.5 text-[10px] text-white/35">
                      <Move className="h-3 w-3 shrink-0" />
                      Позицию задавайте перетаскиванием прямо на холсте
                    </p>

                    {/* ---- Стиль (merged into Параметры) ---- */}
                    <div className="space-y-3 border-t border-white/10 pt-3">
                      {selectedLayer.type === 'text' && (
                        <Field label={t('studio.editor.color')}>
                          <ColorField
                            value={selectedLayer.color}
                            onChange={(color) => patchLayer({ color, textColor: color })}
                          />
                        </Field>
                      )}

                      {selectedLayer.type === 'sticker' && !selectedLayer.src && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-2">
                            <Field label={t('studio.editor.background')}>
                              <ColorField
                                value={selectedLayer.backgroundColor}
                                allowTransparent
                                onChange={(backgroundColor) => patchLayer({ backgroundColor, color: backgroundColor })}
                              />
                            </Field>
                            <Field label={t('studio.editor.textColor')}>
                              <ColorField value={selectedLayer.textColor} onChange={(textColor) => patchLayer({ textColor })} />
                            </Field>
                            {selectedLayer.badgeAccentColor && (
                              <Field label="Акцент">
                                <ColorField
                                  value={selectedLayer.badgeAccentColor}
                                  onChange={(badgeAccentColor) => patchLayer({ badgeAccentColor })}
                                />
                              </Field>
                            )}
                            {selectedLayer.badgeBorderColor && (
                              <Field label="Обводка">
                                <ColorField
                                  value={selectedLayer.badgeBorderColor}
                                  onChange={(badgeBorderColor) => patchLayer({ badgeBorderColor })}
                                />
                              </Field>
                            )}
                          </div>
                          <Field label="Форма бейджа">
                            <div className="grid grid-cols-2 gap-1.5">
                              {([
                                ['pill', 'Пилюля'],
                                ['rounded', 'Скруглённый'],
                                ['soft-square', 'Мягкий'],
                                ['outline', 'Контур'],
                              ] as const).map(([badgeStyle, label]) => (
                                <button
                                  key={badgeStyle}
                                  type="button"
                                  onClick={() => patchLayer({ badgeStyle })}
                                  className={cn(
                                    'h-7 rounded-lg border px-2 text-[10px] font-semibold transition',
                                    (selectedLayer.badgeStyle ?? 'rounded') === badgeStyle
                                      ? 'border-brand-400 bg-brand-500/20 text-white'
                                      : 'border-white/10 bg-white/5 text-white/45 hover:text-white/75'
                                  )}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </Field>
                        </div>
                      )}

                      {selectedLayer.type === 'effect' && (
                        <Field label={t('studio.editor.color')}>
                          <ColorField value={selectedLayer.color} onChange={(color) => patchLayer({ color })} />
                        </Field>
                      )}

                      <Field label={t('studio.editor.opacity')}>
                        <EditorRangeControl
                          min={0}
                          max={1}
                          step={0.05}
                          value={selectedLayerVisual?.opacity ?? selectedLayer.opacity}
                          ariaLabel="Прозрачность слоя"
                          format={(v) => `${Math.round(v * 100)}%`}
                          onChange={(v) => patchLayer({ opacity: v })}
                        />
                      </Field>

                      {(selectedLayer.type === 'text' || (selectedLayer.type === 'sticker' && !selectedLayer.src)) && (
                        <div className="flex gap-1.5">
                          <StyleToggle
                            active={selectedLayer.fontWeight === '800'}
                            onClick={() => patchLayer({ fontWeight: selectedLayer.fontWeight === '800' ? '600' : '800' })}
                          >
                            <Bold className="h-3.5 w-3.5" />
                          </StyleToggle>
                          <StyleToggle
                            active={selectedLayer.fontStyle === 'italic'}
                            onClick={() => patchLayer({ fontStyle: selectedLayer.fontStyle === 'italic' ? 'normal' : 'italic' })}
                          >
                            <Italic className="h-3.5 w-3.5" />
                          </StyleToggle>
                          <StyleToggle active={selectedLayer.underline} onClick={() => patchLayer({ underline: !selectedLayer.underline })}>
                            <Underline className="h-3.5 w-3.5" />
                          </StyleToggle>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => removeLayer(selectedLayer.id)}
                        className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 text-xs font-semibold text-red-400 transition hover:bg-red-500/20"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {t('studio.editor.deleteLayer')}
                      </button>
                    </div>
                  </>
            ) : !selectedClip && !showAudioInspector ? (
              <EmptyHint>{t('studio.editor.selectLayerToEdit')}</EmptyHint>
            ) : null}

            {showAudioInspector && <div className="pt-1">
                  <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
                    <Music className="h-3 w-3" />
                    {t('studio.editor.audioBackground')}
                  </p>
                  {audioTrack ? (
                    <div className="space-y-2.5">
                      <div className="rounded-lg border border-white/10 bg-black/15 px-2.5 py-2">
                        <p className="truncate text-xs font-semibold text-white/80">{audioTrack.fileName ?? 'Audio'}</p>
                        <p className="mt-0.5 text-[10px] text-white/35">Независимая дорожка на таймлайне</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Начало">
                          <EditorRangeControl
                            min={0}
                            max={Math.max(0.1, projectDuration)}
                            step={1 / 30}
                            value={audioTrack.start ?? 0}
                            ariaLabel="Начало аудиодорожки"
                            unit="с"
                            onChange={(start) => patchAudio({ start })}
                          />
                        </Field>
                        <Field label="Длина">
                          <EditorRangeControl
                            min={0.1}
                            max={Math.max(0.1, projectDuration - (audioTrack.start ?? 0))}
                            step={1 / 30}
                            value={audioTrack.timelineDuration ?? audioTrack.duration ?? projectDuration}
                            ariaLabel="Длительность аудиодорожки"
                            unit="с"
                            onChange={(timelineDuration) => patchAudio({ timelineDuration })}
                          />
                        </Field>
                      </div>
                      <Field label="Смещение источника">
                        <EditorRangeControl
                          min={0}
                          max={Math.max(0.1, audioTrack.duration ?? 60)}
                          step={1 / 30}
                          value={audioTrack.sourceOffset ?? 0}
                          ariaLabel="Смещение внутри аудиофайла"
                          unit="с"
                          onChange={(sourceOffset) => patchAudio({ sourceOffset })}
                        />
                      </Field>
                      <Field label={t('studio.editor.audioVolume')}>
                        <EditorRangeControl
                          min={0}
                          max={1}
                          step={0.05}
                          value={audioTrack.volume}
                          ariaLabel="Громкость фоновой музыки"
                          format={(v) => `${Math.round(v * 100)}%`}
                          onChange={(v) => patchAudio({ volume: v })}
                        />
                      </Field>
                      <div className="rounded-lg border border-white/10 bg-black/15 p-2.5">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/40">Уровень</span>
                          <span className="text-[10px] font-semibold tabular-nums text-white/45">
                            {audioTrack.muted ? 'MUTE' : `${Math.round(audioTrack.volume * 100)}%`}
                          </span>
                        </div>
                        <div className="flex h-3 items-end gap-0.5" aria-label="Индикатор уровня аудио">
                          {AUDIO_METER_SEGMENTS.map((segment) => {
                            const threshold = segment / AUDIO_METER_SEGMENTS.length;
                            const active = audioMeterLevel >= threshold;
                            const color = segment > 10 ? 'bg-red-400' : segment > 8 ? 'bg-amber-300' : 'bg-emerald-400';
                            return (
                              <span
                                key={segment}
                                className={cn('h-full flex-1 rounded-[2px] transition-colors', active ? color : 'bg-white/10')}
                              />
                            );
                          })}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => patchAudio({ muted: !audioTrack.muted })}
                          className={cn(
                            'h-8 rounded-lg border text-[11px] font-black transition',
                            audioTrack.muted
                              ? 'border-red-400/60 bg-red-500/15 text-red-300'
                              : 'border-white/10 bg-white/5 text-white/50 hover:text-white'
                          )}
                        >
                          M · Mute
                        </button>
                        <button
                          type="button"
                          onClick={() => patchAudio({ solo: !audioTrack.solo })}
                          className={cn(
                            'h-8 rounded-lg border text-[11px] font-black transition',
                            audioTrack.solo
                              ? 'border-amber-300/60 bg-amber-400/15 text-amber-200'
                              : 'border-white/10 bg-white/5 text-white/50 hover:text-white'
                          )}
                        >
                          S · Solo
                        </button>
                      </div>
                      <Field label="Панорама">
                        <EditorRangeControl
                          min={-1}
                          max={1}
                          step={0.05}
                          value={audioTrack.pan ?? 0}
                          ariaLabel="Стереопанорама аудиодорожки"
                          format={(value) => value === 0 ? 'C' : value < 0 ? `L ${Math.round(Math.abs(value) * 100)}` : `R ${Math.round(value * 100)}`}
                          onChange={(pan) => patchAudio({ pan })}
                        />
                      </Field>
                      <button
                        type="button"
                        onClick={() => patchAudio({ ducking: !audioTrack.ducking })}
                        className={cn(
                          'flex w-full items-center justify-between rounded-lg border px-2.5 py-2 text-left text-xs transition',
                          audioTrack.ducking
                            ? 'border-brand-500 bg-brand-500/15 text-white'
                            : 'border-white/10 bg-white/5 text-white/60 hover:text-white'
                        )}
                      >
                        <span className="flex items-center gap-2"><Volume2 className="h-3.5 w-3.5" /> Приглушать музыку под речь</span>
                        <span className="text-[10px] font-bold">{audioTrack.ducking ? 'ВКЛ' : 'ВЫКЛ'}</span>
                      </button>
                      <Field label={t('studio.editor.audioFadeIn')}>
                        <EditorRangeControl
                          min={0}
                          max={5}
                          step={0.1}
                          value={audioTrack.fadeIn}
                          ariaLabel="Плавное появление фоновой музыки"
                          unit="с"
                          onChange={(v) => patchAudio({ fadeIn: v })}
                        />
                      </Field>
                      <Field label={t('studio.editor.audioFadeOut')}>
                        <EditorRangeControl
                          min={0}
                          max={5}
                          step={0.1}
                          value={audioTrack.fadeOut}
                          ariaLabel="Плавное затухание фоновой музыки"
                          unit="с"
                          onChange={(v) => patchAudio({ fadeOut: v })}
                        />
                      </Field>
                      {audioTrack.id && (
                        <button
                          type="button"
                          onClick={() => removeAudioTrack(audioTrack.id!)}
                          className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 text-xs font-semibold text-red-400 transition hover:bg-red-500/20"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Удалить аудиодорожку
                        </button>
                      )}
                    </div>
                  ) : (
                    <EmptyHint>Аудиодорожка не добавлена</EmptyHint>
                  )}
            </div>}
            {showAudioInspector && <div className="border-t border-white/10 pt-3">
              <Field label={t('studio.editor.audioCrossfade')}>
                <EditorRangeControl
                  min={0}
                  max={2}
                  step={0.1}
                  value={crossfadeDuration}
                  ariaLabel="Кроссфейд между клипами"
                  unit="с"
                  onChange={setCrossfade}
                />
              </Field>
              <p className="mt-1.5 text-[10px] text-white/40">{t('studio.editor.audioCrossfadeHint')}</p>
            </div>}
          </div>
        </section>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Small local presentational helpers
// ---------------------------------------------------------------------------

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-white/40">{label}</p>
      {children}
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <p className="rounded-lg bg-white/[0.02] px-3 py-3 text-center text-[11px] text-white/40">{children}</p>;
}

function ColorField({ value, onChange, allowTransparent }: { value: string; onChange: (value: string) => void; allowTransparent?: boolean }) {
  return <EditorColorPicker value={value} onChange={onChange} allowTransparent={allowTransparent} />;
}

function StyleToggle({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-8 w-9 items-center justify-center rounded-lg border transition',
        active ? 'border-brand-500 bg-brand-500/15 text-brand-200' : 'border-white/10 bg-white/5 text-white/60 hover:text-white'
      )}
    >
      {children}
    </button>
  );
}
